/**
 * lab-filterblok.js — het filter op de veldgegevens bij een labopdracht (tabblad Samenstelling),
 * gedeeld door de pagina en het kaartvenster op een tweede scherm.
 *
 * Het filter zelf woont op de pagina (lab-mengmonsters): die rekent uit welke lagen en potjes
 * passen. Hier staat alleen hoe het eruitziet. De pagina bouwt een `model` (gewone data, zodat
 * het ook via het BroadcastChannel naar het venster kan) en tekent het met dezelfde functie als
 * het venster. De `acties` zijn op de pagina de eigen methodes, in het venster berichten terug
 * naar de pagina. Zo kunnen de twee niet uit elkaar lopen.
 *
 * model: { water, weg, bf, f, tijd: { periode, van, tot, opties: [[id, naam]] }, soils: [[naam, n]], bijm: [[naam, n]], bzb: [[type, n, groep]],
 *          grondwater: [[sleutel, label, n]], waarneming: [[sleutel, label, n]], verpakking: [[soort, n]],
 *          boringen: [[id, naam]], actief, passend, inSel, heeftGekozen }
 * acties: { patch(p), boringFilter(p), wisselBzb(type), wisFilters(), periode({ periode, van, tot }) }
 */
import { html } from 'lit';
import { MIN_INTENSITY_OPTIONS, PACKAGING_LABELS, alsLijst } from './layer-overview.js';
import '../components/filter-keuze.js';

const ICOON_CHEVRON = html`<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
  stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"></path></svg>`;

const meervoud = (n, een, veel) => `${n} ${n === 1 ? een : veel}`;

/** Een vinkknop in filterstijl. */
function vinkje(aan, label, uitleg, bijKlik) {
  return html`
    <button class="vinkknop ${aan ? 'aan' : ''}" @click=${bijKlik} title=${uitleg}>
      <span class="vakje">${aan ? html`<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.4"
        stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7"></path></svg>` : ''}</span>
      ${label}
    </button>`;
}

/**
 * Een diepte in hele centimeters. Geen schuif of vaste stappen: met stappen van 0,5 m werd een
 * boring tot 1,60 m op 1,50 m afgekapt. Leeg is 'geen grens'. Telt bij verlaten of Enter, niet
 * bij elke toets: anders springt het filter al bij het eerste cijfer van '160'.
 */
function cmInvoer(waarde, bijWissel, label) {
  const zet = (e) => {
    const tekst = e.target.value.trim().replace(',', '.');
    const cm = tekst === '' ? null : Math.round(Number(tekst));
    if (cm != null && !(Number.isFinite(cm) && cm >= 0)) { e.target.value = waarde ?? ''; return; }
    if (cm !== waarde) bijWissel(cm);
  };
  return html`
    <input class="cm" type="number" min="0" step="1" inputmode="numeric" placeholder="—"
           aria-label=${label} .value=${waarde ?? ''}
           @change=${zet} @keydown=${(e) => { if (e.key === 'Enter') zet(e); }}>`;
}

/** Een keuzelijst in filterstijl: groen omrand zodra er iets gekozen is. */
function keuze(waarde, leeg, opties, bijWissel) {
  return html`
    <span class="keuze ${waarde ? 'aan' : ''}">
      <select .value=${waarde} @change=${(e) => bijWissel(e.target.value)}>
        <option value="">${leeg}</option>
        ${opties.map(([v, label]) => html`<option value=${v} ?selected=${waarde === v}>${label}</option>`)}
      </select>
      ${ICOON_CHEVRON}
    </span>`;
}

/** Eén keuzelijst met vinkjes (filter-keuze); `modus` leeg = geen keuze één/allemaal. */
function keuzelijst(leeg, meervoud, zoekLabel, items, gekozen, modus, bijWijzig) {
  return html`
    <filter-keuze leeg=${leeg} meervoud=${meervoud} zoekLabel=${zoekLabel || 'Zoek'} modus=${modus}
      .items=${items} .gekozen=${gekozen || []}
      @wijzig=${(e) => bijWijzig(e.detail.gekozen, e.detail.modus)}></filter-keuze>`;
}

/**
 * Wat er aan staat in de keuzelijsten, als chips met een kruisje: zo zie je het filter zonder
 * elk menu open te klikken. Per filter één groepje, met 'één ervan' of 'allemaal' erbij als
 * het er meer zijn.
 */
function renderActief(m, a) {
  const f = m.f;
  const naamVan = (lijst, waarde, i = 0, j = 1) => lijst.find((x) => x[i] === waarde)?.[j] ?? waarde;
  const groepen = [
    { titel: 'Hoofdgrondsoort', waarden: f.soils, label: (v) => v, weg: (v) => a.patch({ soils: f.soils.filter((x) => x !== v) }) },
    { titel: 'Bijmenging', waarden: f.admixtures, modus: f.admixtureMode, label: (v) => v,
      weg: (v) => a.patch({ admixtures: f.admixtures.filter((x) => x !== v) }) },
    { titel: 'Bijzondere bestanddelen', waarden: f.include, modus: f.mode, label: (v) => v,
      weg: (v) => a.wisselBzb(v) },
    { titel: 'Zonder', waarden: f.exclude, label: (v) => v, weg: (v) => a.wisselBzb(v), zonder: true },
    { titel: 'Grondwater', waarden: alsLijst(f.groundwater), label: (v) => naamVan(m.grondwater, v),
      weg: (v) => a.patch({ groundwater: alsLijst(f.groundwater).filter((x) => x !== v) }) },
    { titel: 'Waarneming', waarden: alsLijst(f.observation), modus: f.observationMode, label: (v) => naamVan(m.waarneming, v),
      weg: (v) => a.patch({ observation: alsLijst(f.observation).filter((x) => x !== v) }) },
    { titel: 'Boring', waarden: f.boringIds, label: (v) => naamVan(m.boringen, v),
      weg: (v) => a.patch({ boringIds: f.boringIds.filter((x) => x !== v) }) },
  ].filter((g) => g.waarden.length);
  if (!groepen.length) return '';
  return html`
    <span class="rijlabel">Actief</span>
    <div class="rij actief">
      ${groepen.map((g) => html`
        <span class="actiefgroep">
          <span class="actieftitel">${g.titel}${g.waarden.length > 1 && g.modus ? ` (${g.modus === 'all' ? 'allemaal' : 'één ervan'})` : ''}:</span>
          ${g.waarden.map((v) => html`
            <button class="actiefchip ${g.zonder ? 'zonder' : ''}" title="Uit het filter halen" @click=${() => g.weg(v)}>
              ${g.label(v)} <span aria-hidden="true">×</span>
            </button>`)}
        </span>`)}
      <button class="tekstknop" @click=${() => a.wisFilters()}>Wis</button>
    </div>`;
}

/**
 * Tijd: van wanneer de boringen zijn. Tegels voor de gewone keuzes, en 'Aangepast' met een
 * begin- en einddatum (beide mogen leeg: dan is die kant open). Ook in water-modus.
 */
function renderTijd(t, a) {
  if (!t) return '';
  const zet = (patch) => a.periode({ periode: t.periode, van: t.van, tot: t.tot, ...patch });
  return html`
    <span class="rijlabel">Tijd</span>
    <div class="rij">
      <div class="chips">
        ${t.opties.map(([id, naam]) => html`
          <button class="bzbchip ${t.periode === id ? 'aan' : ''}" aria-pressed=${t.periode === id}
                  @click=${() => zet({ periode: id })}>${naam}</button>`)}
      </div>
      ${t.periode === 'aangepast' ? html`
        <label class="cmveld ${t.van || t.tot ? 'aan' : ''}">
          Van <input class="datum" type="date" .value=${t.van || ''} max=${t.tot || ''} aria-label="Begindatum"
                     @change=${(e) => zet({ van: e.target.value || null })}>
          tot en met <input class="datum" type="date" .value=${t.tot || ''} min=${t.van || ''} aria-label="Einddatum"
                     @change=${(e) => zet({ tot: e.target.value || null })}>
        </label>` : ''}
    </div>`;
}

/** Het filterblok. Zie de kop van dit bestand voor `m` en `a`. */
export function renderFilterblok(m, a) {
  const bf = m.bf;
  if (m.water) {
    return html`
      <div class="filterblok">
        ${renderTijd(m.tijd, a)}
        <span class="rijlabel">Boringen</span>
        <div class="rij">
          ${vinkje(bf.withSamplesOnly, 'Alleen boringen met watermonsters',
            'Deze labopdracht is voor grondwater: alleen boringen met een bemonsterde peilbuis doen mee.',
            () => a.boringFilter({ withSamplesOnly: !bf.withSamplesOnly }))}
          <span class="spacer"></span>
          ${m.weg ? html`<span class="filterregel">${meervoud(m.weg, 'boring', 'boringen')} verborgen</span>` : ''}
        </div>
      </div>`;
  }
  const f = m.f;
  const bemonsterd = f.sampledOnly;
  return html`
    <div class="filterblok">
      ${renderTijd(m.tijd, a)}
      <!-- Twee dingen die je niet moet verwarren: welke boringen meedoen (hier), en welk
           traject je daarbinnen bemonstert (de regel eronder). -->
      <span class="rijlabel">Boringen</span>
      <div class="rij">
        ${vinkje(bf.withSamplesOnly, 'Alleen boringen met monsters',
          'Geotechnische boringen zonder potjes doen niet mee bij het samenstellen van mengmonsters.',
          () => a.boringFilter({ withSamplesOnly: !bf.withSamplesOnly }))}
        <label class="cmveld ${bf.minEndDepthCm != null ? 'aan' : ''}"
               title="Alleen boringen die minstens tot deze diepte zijn gezet">
          Einddiepte minstens
          ${cmInvoer(bf.minEndDepthCm, (v) => a.boringFilter({ minEndDepthCm: v }), 'Minimale einddiepte in cm onder maaiveld')}
          cm –mv
        </label>
        <span class="spacer"></span>
        ${m.weg ? html`<span class="filterregel">${meervoud(m.weg, 'boring', 'boringen')} verborgen</span>` : ''}
      </div>

      <span class="rijlabel">Diepte</span>
      <div class="rij">
        <label class="cmveld ${f.minDepthCm != null || f.maxDepthCm != null ? 'aan' : ''}"
               title="Alleen lagen en potjes die dit traject overlappen, in cm onder maaiveld. Vanaf 5 cm laat een asfaltlaag van 0–5 cm weg.">
          Van
          ${cmInvoer(f.minDepthCm, (v) => a.patch({ minDepthCm: v }), 'Bovenkant van de diepte in cm onder maaiveld')}
          tot
          ${cmInvoer(f.maxDepthCm, (v) => a.patch({ maxDepthCm: v }), 'Onderkant van de diepte in cm onder maaiveld')}
          cm –mv
        </label>
        ${f.minDepthCm != null && f.maxDepthCm != null && f.minDepthCm >= f.maxDepthCm
          ? html`<span class="dlfout">"Van" moet boven "tot" liggen.</span>` : ''}
      </div>

      <!-- Alle keuzes over de laag in één regel, elk als keuzelijst met vinkjes (filter-keuze):
           ook 90+ bijzondere bestanddelen nemen zo niet meer dan één knop in. Wat er aan staat,
           staat in de regel eronder, met een kruisje per keuze. -->
      <span class="rijlabel">Laag</span>
      <div class="rij">
        ${keuzelijst('Alle hoofdgrondsoorten', 'hoofdgrondsoorten', 'Zoek hoofdgrondsoort',
          m.soils.map(([v, n]) => ({ waarde: v, label: v, aantal: n })), f.soils, '',
          (g) => a.patch({ soils: g }))}
        ${keuzelijst('Alle bijmengingen', 'bijmengingen', 'Zoek bijmenging',
          m.bijm.map(([v, n]) => ({ waarde: v, label: v, aantal: n })), f.admixtures, f.admixtureMode || 'any',
          (g, modus) => a.patch({ admixtures: g, admixtureMode: modus }))}
        ${keuzelijst('Alle bijzondere bestanddelen', 'bijzondere bestanddelen', 'Zoek bestanddeel',
          m.bzb.map(([t, n, groep]) => ({ waarde: t, label: t, aantal: n, groep })), f.include, f.mode || 'any',
          (g, modus) => a.patch({ include: g, mode: modus }))}
        ${keuzelijst('Boven en onder grondwater', 'liggingen', '',
          m.grondwater.map(([k, label, n]) => ({ waarde: k, label, aantal: n })), alsLijst(f.groundwater), '',
          (g) => a.patch({ groundwater: g }))}
        ${keuzelijst('Elke waarneming', 'waarnemingen', '',
          m.waarneming.map(([k, label, n]) => ({ waarde: k, label, aantal: n })), alsLijst(f.observation), f.observationMode || 'any',
          (g, modus) => a.patch({ observation: g, observationMode: modus }))}
        ${keuzelijst('Alle boringen', 'boringen', 'Zoek boring',
          m.boringen.map(([id, naam]) => ({ waarde: id, label: naam })), f.boringIds, '',
          (g) => a.patch({ boringIds: g }))}
        <span class="spacer"></span>
        ${vinkje(bemonsterd, 'Alleen bemonsterde lagen',
          'Je stelt hier mengmonsters samen; een laag zonder potje kun je toch niet meenemen. Zet uit om het hele profiel te zien.',
          () => a.patch({ sampledOnly: !bemonsterd }))}
      </div>
      ${renderActief(m, a)}

      <!-- Soort verpakking: potten, steekbussen, emmers. Meerdere tegelijk mag; het lab
           behandelt een steekbus en een emmer anders, dus vaak wil je ze apart samenstellen. -->
      <span class="rijlabel">Verpakking</span>
      <div class="rij">
        <div class="chips">
          ${m.verpakking.length ? m.verpakking.map(([v, n]) => {
            const aan = f.packaging.includes(v);
            return html`
              <button class="bzbchip ${aan ? 'aan' : ''}"
                      title=${aan ? 'Klik om uit te zetten' : `Alleen potjes in een ${(PACKAGING_LABELS[v] || v).toLowerCase()}`}
                      @click=${() => a.patch({ packaging: aan ? f.packaging.filter((x) => x !== v) : [...f.packaging, v] })}>
                <span>${PACKAGING_LABELS[v] || v}</span><span class="aantal">${n}</span>
              </button>`;
          }) : html`<span class="leegregel">Nog geen potjes in deze periode.</span>`}
        </div>
      </div>

      <span class="rijlabel">Verfijnen</span>
      <div class="rij">
        ${keuze(f.minIntensity, 'Elke intensiteit', MIN_INTENSITY_OPTIONS.map((o) => [o.value, o.label]),
          (v) => a.patch({ minIntensity: v }))}
        <span class="spacer"></span>
        ${m.actief && m.heeftGekozen ? html`
          <span class="filterregel">${m.passend} van ${meervoud(m.inSel, 'laag past', 'lagen passen')}</span>` : ''}
        ${m.actief ? html`<button class="tekstknop groen" @click=${() => a.wisFilters()}>Wis filters</button>` : ''}
      </div>
    </div>
  `;
}

/** De opmaak van het filterblok; in de static styles van beide componenten. */
export const FILTERBLOK_CSS = `
    .spacer { flex:1; min-width:8px; }
    .rij.actief { gap:10px; row-gap:6px; }
    .actiefgroep { display:inline-flex; align-items:center; gap:5px; flex-wrap:wrap; }
    .actieftitel { font-size:.74rem; color:var(--mv-text-sub); }
    .actiefchip { display:inline-flex; align-items:center; gap:5px; padding:3px 9px; border-radius:7px;
      border:1.5px solid var(--mv-primary); background:var(--mv-primary-tint); color:var(--mv-primary-hover);
      font:inherit; font-size:.74rem; font-weight:800; cursor:pointer; }
    .actiefchip span { font-size:.9rem; line-height:1; }
    .actiefchip:hover { background:var(--mv-surface); }
    .actiefchip.zonder { border-color:var(--mv-danger); background:var(--mv-danger-bg); color:var(--mv-danger); }
    .dlfout { flex-basis:100%; color:var(--mv-danger,#c0392b); font-size:.72rem; }
    .filterblok { display:grid; grid-template-columns:minmax(0,104px) minmax(0,1fr); column-gap:12px;
                  padding:0 20px; border-bottom:1px solid var(--mv-border-soft); }
    .rijlabel { font-size:.7rem; color:var(--mv-text-muted); padding:10px 0; line-height:1.3; }
    .rij { display:flex; align-items:center; gap:6px; flex-wrap:wrap; padding:5px 0; min-width:0; }
    .cmveld { display:inline-flex; align-items:center; gap:6px; font-size:.74rem; font-weight:700; color:var(--mv-text-sub); }
    .cmveld input.cm { width:4.6rem; box-sizing:border-box; padding:5px 8px; border-radius:8px; border:1.5px solid var(--mv-border);
      background:var(--mv-surface); color:var(--mv-text); font:inherit; font-size:.76rem; font-weight:700; text-align:right; }
    .cmveld input.cm:focus { outline:none; border-color:var(--mv-primary); }
    .cmveld.aan input.cm { border-color:var(--mv-primary); background:var(--mv-primary-tint); }
    .cmveld input.datum { box-sizing:border-box; padding:4px 8px; border-radius:8px; border:1.5px solid var(--mv-border);
      background:var(--mv-surface); color:var(--mv-text); font:inherit; font-size:.76rem; font-weight:700; }
    .cmveld input.datum:focus { outline:none; border-color:var(--mv-primary); }
    .cmveld.aan input.datum { border-color:var(--mv-primary); background:var(--mv-primary-tint); }
    .rij .dlfout { flex-basis:auto; }
    .rij.kolom { flex-direction:column; align-items:stretch; gap:7px; }
    .rijlabel:nth-of-type(n+2), .rijlabel:nth-of-type(n+2) + .rij { border-top:1px dashed var(--mv-border-soft); }
    .keuze { position:relative; flex:0 0 auto; display:inline-flex; }
    .keuze select { padding:6px 30px 6px 10px; border-radius:8px; border:1.5px solid var(--mv-border);
      background:var(--mv-surface); color:var(--mv-text); appearance:none; -webkit-appearance:none;
      font:inherit; font-size:.76rem; font-weight:700; cursor:pointer; outline:none; max-width:220px; }
    .keuze select:hover { border-color:var(--mv-primary); }
    .keuze.aan select { border-color:var(--mv-primary); background:var(--mv-primary-tint); }
    .keuze svg { position:absolute; right:10px; top:50%; transform:translateY(-50%); pointer-events:none; color:var(--mv-text-sub); }
    .vinkknop { display:inline-flex; align-items:center; gap:7px; padding:5px 10px; border-radius:8px;
      border:1.5px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text);
      font:inherit; font-size:.76rem; font-weight:700; cursor:pointer; white-space:nowrap; }
    .vinkknop .vakje { width:15px; height:15px; border-radius:4px; border:1.5px solid var(--mv-border-strong, #c9d2c9);
      background:var(--mv-surface); display:flex; align-items:center; justify-content:center; }
    .vinkknop.aan { border-color:var(--mv-primary); background:var(--mv-primary-tint); }
    .vinkknop.aan .vakje { background:var(--mv-primary); border-color:var(--mv-primary); }
    .chips { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
    .bzbchip { display:inline-flex; align-items:baseline; gap:4px; padding:5px 9px; border-radius:7px;
      border:1.5px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text);
      font:inherit; font-size:.74rem; font-weight:800; cursor:pointer; }
    .bzbchip:hover { border-color:var(--mv-primary); }
    .bzbchip .aantal { font-size:.68rem; font-weight:700; color:var(--mv-text-muted); }
    .bzbchip.aan { background:#22371f; border-color:#22371f; color:#fff; }
    .bzbchip.aan .aantal { color:#c9d9c4; }
    .bzbchip.zonder { background:var(--mv-danger-bg); border-color:var(--mv-danger); color:var(--mv-danger); text-decoration:line-through; }
    .uitleg, .leegregel { font-size:.68rem; color:var(--mv-text-muted); }
    .segment { display:inline-flex; border:1.5px solid var(--mv-border); border-radius:8px; overflow:hidden; flex:0 0 auto; }
    .segment button { padding:5px 11px; border:none; background:var(--mv-surface); color:var(--mv-text);
      font:inherit; font-size:.76rem; font-weight:800; cursor:pointer; white-space:nowrap; }
    .segment button.aan { background:#22371f; color:#fff; }
    .filterregel { font-size:.72rem; color:var(--mv-text-muted); }
    .tekstknop { border:none; background:none; padding:0 2px; font:inherit; font-size:.74rem; font-weight:800;
      color:var(--mv-text-sub); cursor:pointer; white-space:nowrap; }
    .tekstknop:disabled { opacity:.4; cursor:default; }
    .tekstknop:hover:not(:disabled) { color:var(--mv-primary); }
    .tekstknop.groen { color:var(--mv-primary); }
    .tekstknop.groen:hover { color:var(--mv-primary-hover); }
`;
